import React from "react"
import { TextField } from "@material-ui/core"
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete"
import AddIcon from "@material-ui/icons/Add"

import { themeColors } from "letalkui"

import { CustomField } from "@/protocols/customField"
import { ExtraColumnMappingDecisionType } from "@/protocols/clientCatalogImportProtocol"

const systemColors = themeColors.colors.system

type ExistingFieldOption = {
	optionKind: "existing"
	customFieldId: number
	displayName: string
}

type NewFieldOption = {
	optionKind: "new"
	fieldName: string
	displayName: string
}

type CustomFieldOption = ExistingFieldOption | NewFieldOption

const autocompleteFilter = createFilterOptions<CustomFieldOption>()

type Props = {
	availableCustomFields: CustomField[]
	unavailableFieldNames?: string[]
	value: ExtraColumnMappingDecisionType
	suggestedFieldName?: string
	onChange: (decision: ExtraColumnMappingDecisionType) => void
	disabled?: boolean
}

const CustomFieldCombobox = (props: Props) => {
	const { availableCustomFields, unavailableFieldNames, value, suggestedFieldName, onChange, disabled } = props

	const existingOptions: ExistingFieldOption[] = availableCustomFields.map((customField) => ({
		optionKind: "existing",
		customFieldId: customField.id,
		displayName: customField.display_name
	}))

	const selectedOption: CustomFieldOption | null = (() => {
		if (!value) {
			return null
		}
		if (value.kind === "existing") {
			const matchedOption = existingOptions.find((option) => option.customFieldId === value.customFieldId)

			if (matchedOption) {
				return matchedOption
			}

			return null
		}
		return { optionKind: "new", fieldName: value.fieldName, displayName: value.fieldName }
	})()

	const handleChange = (_event: React.SyntheticEvent, selected: CustomFieldOption | null) => {
		if (!selected) {
			onChange(null)
			return
		}
		if (selected.optionKind === "new") {
			onChange({ kind: "new", fieldName: selected.fieldName })
			return
		}
		onChange({ kind: "existing", customFieldId: selected.customFieldId, displayName: selected.displayName })
	}

	return (
		<Autocomplete
			value={selectedOption}
			options={existingOptions}
			disabled={disabled}
			fullWidth
			clearOnBlur
			selectOnFocus
			handleHomeEndKeys
			getOptionLabel={(option) => option.displayName}
			getOptionSelected={(option, selected) => {
				if (option.optionKind === "existing" && selected.optionKind === "existing") {
					return option.customFieldId === selected.customFieldId
				}
				if (option.optionKind === "new" && selected.optionKind === "new") {
					return option.fieldName === selected.fieldName
				}
				return false
			}}
			filterOptions={(options, filterState) => {
				const filtered = autocompleteFilter(options, filterState)
				const typedValue = filterState.inputValue.trim()
				const candidateFieldName = typedValue || (suggestedFieldName || "").trim()
				const alreadyExists = options.some(
					(option) => option.displayName.toLowerCase() === candidateFieldName.toLowerCase()
				)
				const isNameUsedByAnotherColumn = (unavailableFieldNames || []).some(
					(usedFieldName) => usedFieldName.toLowerCase() === candidateFieldName.toLowerCase()
				)

				if (candidateFieldName && !alreadyExists && !isNameUsedByAnotherColumn) {
					filtered.push({ optionKind: "new", fieldName: candidateFieldName, displayName: candidateFieldName })
				}

				return filtered
			}}
			renderOption={(option) => {
				if (option.optionKind === "new") {
					return (
						<span style={{ display: "flex", alignItems: "center", gap: 6, color: systemColors.purple[500], fontWeight: 600 }}>
							<AddIcon style={{ fontSize: 18, color: systemColors.purple[500] }} />
							Criar campo &quot;{option.fieldName}&quot;
						</span>
					)
				}
				return option.displayName
			}}
			onChange={handleChange}
			renderInput={(params) => (
				<TextField
					{...params}
					variant="outlined"
					placeholder="Selecionar ou criar campo"
					size="small"
				/>
			)}
			noOptionsText="Digite para criar um novo campo"
		/>
	)
}

export default CustomFieldCombobox
